"use client";

import React from "react";
import { motion } from "framer-motion";
import Link from "next/link";
import { Platform, PlatformFeature, resolvePlatformIcon } from "@/data/platforms";
import { PremiumCard } from "@/components/ui/PremiumCard";

interface RelatedProductsGridProps {
  platform: Platform;
}

export function RelatedProductsGrid({ platform }: RelatedProductsGridProps) {
  return (
    <section className="py-14 md:py-20 bg-white">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Section header */}
        <div className="mb-8 md:mb-10">
          <h2 className="text-2xl font-extrabold tracking-tight text-slate-900 md:text-3xl">
            {platform.product} Family
          </h2>
          <p className="mt-2 max-w-2xl text-sm text-slate-500 md:text-base">
            Explore every sub-product under the {platform.product} family — purpose-built for {platform.category.toLowerCase()}.
          </p>
        </div>

        {/* Products Grid */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 sm:gap-5">
          {platform.features.map((feature, idx) => {
            const Icon = resolvePlatformIcon(feature.icon);
            return (
              <motion.div
                key={feature.href}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{
                  duration: 0.5,
                  delay: idx * 0.07,
                  ease: [0.22, 1, 0.36, 1],
                }}
                className="h-full"
              >
                <PremiumCard
                  customIcon={
                    <span style={{ color: platform.color }}>
                      {Icon && <Icon className="h-8 w-8" />}
                    </span>
                  }
                  title={feature.name}
                  description={feature.desc}
                  ctaLabel="Explore"
                  href={feature.href}
                  color="blue"
                  className="h-full"
                />
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
